// MSW request bodies are typed as `unknown`; narrowing to the domain generic is required at the boundary.
/* eslint-disable @typescript-eslint/no-unsafe-type-assertion */
import {
  MOCK_AAE_FACTORY_ID,
  MOCK_AAE_MACHINE_PARK,
  MOCK_SHOWCASE_MACHINE_PARK,
  type MachineDatasheet,
  type MachinePark,
} from '@indutrust/factory-model';
import {
  notifyMachineParkDatasheetUploaded,
  onMachineCatalogDecision,
} from '@indutrust/machine-data';
import type { RequestHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import { denyFactoryMutationForViewer } from '../../+mock/factory-team-role-mutation.util';

type InternalMachine = MachinePark & {
  _factoryId: number;
};

type MachineParkMockState = {
  datasheets: (MachineDatasheet & { _machineId: number })[];
  machines: InternalMachine[];
};

const nextId = (rows: readonly { id: number }[]): number =>
  rows.length === 0 ? 1 : Math.max(...rows.map((r) => r.id)) + 1;

function createSeededDatasheets(): (MachineDatasheet & {
  _machineId: number;
})[] {
  return [
    {
      id: 1,
      _machineId: 1,
      name: 'DMG-MORI-DMU-spec.pdf',
      url: 'https://cdn.example.com/datasheets/dmg-mori-dmu-spec.pdf',
    },
    {
      id: 2,
      _machineId: 101,
      name: 'Trumpf-TruLaser-7000-spec.pdf',
      url: 'https://cdn.example.com/datasheets/trumpf-trulaser-7000-spec.pdf',
    },
    {
      id: 3,
      _machineId: 104,
      name: 'Waterjet-Co-bevel-spec.pdf',
      url: 'https://cdn.example.com/datasheets/waterjet-co-bevel-spec.pdf',
    },
    {
      id: 4,
      _machineId: 119,
      name: 'HAAS-VF-4-spec.pdf',
      url: 'https://cdn.example.com/datasheets/haas-vf4-spec.pdf',
    },
    {
      id: 5,
      _machineId: 123,
      name: 'Trumpf-TruBend-5320-spec.pdf',
      url: 'https://cdn.example.com/datasheets/trumpf-trubend-5320-spec.pdf',
    },
  ];
}

function createSeededMachines(): InternalMachine[] {
  return [
    ...MOCK_SHOWCASE_MACHINE_PARK.map((machine) => ({
      ...machine,
      _factoryId: 1,
    })),
    ...MOCK_AAE_MACHINE_PARK.map((machine) => ({
      ...machine,
      _factoryId: MOCK_AAE_FACTORY_ID,
    })),
  ];
}

function createMachineParkMockState(): MachineParkMockState {
  return {
    datasheets: createSeededDatasheets(),
    machines: createSeededMachines(),
  };
}

function datasheetsForMachine(
  state: MachineParkMockState,
  machineId: number
): MachineDatasheet[] {
  return state.datasheets
    .filter((d) => d._machineId === machineId)
    .map(({ _machineId: _mid, ...d }) => d);
}

function toResponse(
  state: MachineParkMockState,
  machine: InternalMachine
): MachinePark {
  const { _factoryId: _fid, ...rest } = machine;
  return {
    ...rest,
    datasheets: datasheetsForMachine(state, machine.id),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Unwraps `{ machine_park: … }` when present (legacy write envelope). */
function parseMachineParkBody(body: unknown): Partial<MachinePark> {
  if (!isRecord(body)) {
    return {};
  }
  const nested = body['machine_park'];
  if (isRecord(nested)) {
    return nested;
  }
  return body;
}

function wireCatalogDecisionBridge(state: MachineParkMockState): () => void {
  return onMachineCatalogDecision((decision) => {
    const idx = state.machines.findIndex(
      (m) => m.id === decision.machineParkId
    );
    if (idx === -1) {
      return;
    }
    if (decision.decision === 'approved') {
      state.machines[idx] = {
        ...state.machines[idx],
        machine_catalog_id: decision.catalogEntryId,
        pending_catalog_review: false,
      };
      return;
    }
    state.machines[idx] = {
      ...state.machines[idx],
      pending_catalog_review: false,
    };
  });
}

/**
 * MSW handlers for machine-park CRUD on the legacy nested path:
 * `/factories/:factoryId/capabilities/:capabilityId/machine_parks`.
 *
 * Pass a shared {@link MachineParkMockState} when composing with
 * {@link machineParkDatasheetHandlers} so both see the same in-memory rows.
 */
export function machineParkHandlers(
  baseUrl: string,
  state: MachineParkMockState = createMachineParkMockState()
): RequestHandler[] {
  wireCatalogDecisionBridge(state);

  const base = `${baseUrl}/factories/:factoryId/capabilities/:capabilityId/machine_parks`;

  return [
    http.get(base, ({ params }) => {
      const factoryId = Number(params['factoryId']);
      const result = state.machines
        .filter((m) => m._factoryId === factoryId)
        .map((machine) => toResponse(state, machine));
      return HttpResponse.json(result);
    }),

    http.post(base, async ({ params, request }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const factoryId = Number(params['factoryId']);
      const body = parseMachineParkBody(await request.json());
      const created: InternalMachine = {
        id: nextId(state.machines),
        _factoryId: factoryId,
        name: body.name ?? '',
        brand: body.brand ?? '',
        quantity: body.quantity ?? 1,
        capability_ids: body.capability_ids ?? [],
        machine_catalog_id: body.machine_catalog_id ?? null,
        pending_catalog_review: body.pending_catalog_review ?? false,
        datasheets: [],
      };
      state.machines.push(created);
      return HttpResponse.json(toResponse(state, created), { status: 201 });
    }),

    http.patch(`${base}/:id`, async ({ params, request }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const id = Number(params['id']);
      const body = parseMachineParkBody(await request.json());
      const idx = state.machines.findIndex((m) => m.id === id);
      if (idx === -1) {
        return HttpResponse.json({ error: 'not found' }, { status: 404 });
      }
      state.machines[idx] = { ...state.machines[idx], ...body, id };
      return HttpResponse.json(toResponse(state, state.machines[idx]));
    }),

    http.delete(`${base}/:id`, ({ params }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const id = Number(params['id']);
      const idx = state.machines.findIndex((m) => m.id === id);
      if (idx !== -1) {
        state.machines.splice(idx, 1);
      }
      return HttpResponse.json({}, { status: 200 });
    }),
  ];
}

/**
 * MSW handlers for machine-park datasheets on the flat path
 * `/factories/:factoryId/machine_parks/:machineId/datasheets` — no legacy
 * equivalent; used as a local-dev gap mock alongside the real backend.
 */
export function machineParkDatasheetHandlers(
  baseUrl: string,
  state: MachineParkMockState = createMachineParkMockState()
): RequestHandler[] {
  wireCatalogDecisionBridge(state);
  const base = `${baseUrl}/factories/:factoryId/machine_parks`;

  return [
    http.post(`${base}/:machineId/datasheets`, async ({ params, request }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const machineId = Number(params['machineId']);
      const formData = await request.formData();
      const name = (formData.get('name') as string | null) ?? 'datasheet';
      const created: MachineDatasheet & { _machineId: number } = {
        id: nextId(state.datasheets),
        _machineId: machineId,
        name,
        url: `https://cdn.example.com/datasheets/${Date.now()}-${name}`,
      };
      state.datasheets.push(created);

      const { _machineId: _mid2, ...response } = created;
      notifyMachineParkDatasheetUploaded(machineId, response);
      return HttpResponse.json(response, { status: 201 });
    }),

    http.delete(`${base}/:machineId/datasheets/:datasheetId`, ({ params }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const datasheetId = Number(params['datasheetId']);
      const idx = state.datasheets.findIndex((d) => d.id === datasheetId);
      if (idx !== -1) {
        state.datasheets.splice(idx, 1);
      }
      return HttpResponse.json({}, { status: 200 });
    }),
  ];
}

/**
 * Nested CRUD + flat datasheet handlers sharing one in-memory park.
 */
export function machineParkCombinedHandlers(baseUrl: string): RequestHandler[] {
  const state = createMachineParkMockState();
  return [
    ...machineParkHandlers(baseUrl, state),
    ...machineParkDatasheetHandlers(baseUrl, state),
  ];
}
